import Calendar from '@event-calendar/core';
import TimeGrid from '@event-calendar/time-grid';
import DayGrid from '@event-calendar/day-grid';
import InteractionPlugin from '@event-calendar/interaction';
import ResourceTimeGridPlugin from '@event-calendar/resource-time-grid';
import ResourceTimelineWeekPlugin from '@event-calendar/resource-timeline';
import moment from 'moment';
import {initFullCalendar} from "./full-calendar.js";
import {isEmpty} from "lodash";
import tippy from "tippy.js";

const fetchDoctors = async () => {
    try {
        let params = new URLSearchParams(window.location.search);
        let newParams = new URLSearchParams();

        for (let [key, value] of params.entries()) {
            if (key === "doctor_id_in[]") {
                newParams.append("id_in[]", value); // rename
            }
        }

        const response = await axios.get(`${window.location.origin}/doctor/portal/list?${newParams.toString()}`);
        return response.data.data;
    } catch (error) {
        return [];
    }
}

const initCalendar = async () => {
    let calendarEl = document.getElementById('vkurko-calendar');
    if (!calendarEl) return;

    initFullCalendar();

    const doctorResources = await fetchDoctors();
    const isCanCreate = calendarEl.getAttribute('data-can-create');
    const isCanUpdate = calendarEl.getAttribute('data-can-update');
    let size = getSize(calendarEl.getAttribute('data-calendar-size'));
    let slotMinTime = calendarEl.getAttribute('data-calendar-start');
    let slotMaxTime = calendarEl.getAttribute('data-calendar-end');
    let slotDuration = calendarEl.getAttribute('data-calendar-min-interval');
    let firstDay = getFirstDay(calendarEl.getAttribute('data-calendar-week-start-from'));

    let ec = new Calendar({
        target: calendarEl,
        props: {
            plugins: [InteractionPlugin, TimeGrid, DayGrid, ResourceTimeGridPlugin, ResourceTimelineWeekPlugin],
            options: {
                view: 'timeGridDay',
                slotMinTime: !isEmpty(slotMinTime) ? slotMinTime : '00:00',
                slotMaxTime: !isEmpty(slotMaxTime) ? slotMaxTime : '24:00',
                slotDuration: !isEmpty(slotDuration) ? '00:' + slotDuration : '00:30',
                slotHeight: size ?? 30,
                firstDay: firstDay ?? 1,
                allDayContent: 'All Day',
                dayMaxEvents: true,
                selectable: true,
                lazyFetching: false,
                headerToolbar: {
                    start: 'prev,next today search',
                    center: 'title,datepicker',
                    end: 'timeGridDay,timeGridWeek,dayGridMonth,resourceTimeGridDay',
                },
                buttonText: {
                    today: 'Today',
                    timeGridDay: 'Day',
                    timeGridWeek: 'Week',
                    dayGridMonth: 'Month',
                    resourceTimeGridDay: 'Calendars',
                },
                customButtons: {
                    search: {
                        text: {
                            html: '<i class="fa-solid fa-magnifying-glass"></i>'
                        },
                        click() {
                            const modalEl = document.querySelector('#appointment-datatable-modal');
                            const modal = KTModal.getInstance(modalEl);
                            modal.show();
                        },
                    },
                    datepicker: {
                        text: {
                            html: '<i class="fa-solid fa-arrow-down"></i>'
                        },
                    }
                },
                resources: doctorResources,
                eventSources: [
                    {
                        url: `${window.location.origin}/appointment/portal/list`,
                        method: 'GET',
                        extraParams: function () {
                            const queryParams = new URLSearchParams(window.location.search);
                            const params = Object.fromEntries(queryParams.entries());
                            const doctorFilter = queryParams.getAll('doctor_id_in[]');

                            if (window.mainCalendarInstance) {
                                params.view = window.mainCalendarInstance.getOption('view');
                            }

                            if (!isEmpty(doctorFilter)) {
                                delete params['doctor_id_in[]'];
                                params.doctor_id_in = doctorFilter;
                            }

                            return params;
                        },
                    },
                ],
                viewDidMount(info) {
                    setSize(size);
                },
                datesSet: function (info) {
                    const titleEls = document.querySelectorAll('.ec-title, .ec-datepicker');
                    titleEls.forEach(titleEl => {
                        titleEl.addEventListener('click', function () {
                            const modalEl = document.querySelector('#full-calendar-modal');
                            const modal = KTModal.getInstance(modalEl);
                            modal.show();
                        });
                    });
                },
                dateClick: function (info) {
                    if (isCanCreate) {
                        let viewType = info.view.type;
                        if (viewType === 'dayGridMonth') {
                            ec.setOption('view', 'resourceTimeGridDay');
                        } else {
                            let slotDuration = ec.getOption('slotDuration').seconds;
                            let startTime = new Date(info.date);
                            let endTime = new Date(info.date.getTime() + (slotDuration * 1000));

                            if (info.allDay) {
                                startTime = moment(startTime).startOf('day').format('YYYY-MM-DD HH:mm:ss');
                                endTime = moment(endTime).startOf('day').add(1, 'day').format('YYYY-MM-DD HH:mm:ss');
                            } else {
                                startTime = moment(startTime).format('YYYY-MM-DD HH:mm:ss');
                                endTime = moment(endTime).format('YYYY-MM-DD HH:mm:ss');
                            }

                            let route = window.location.origin + `/appointment/create?start_time=${startTime}&end_time=${endTime}`;

                            if (info.allDay) {
                                route = route + '&all_day=true';
                            }
                            if (info.view.type === 'resourceTimeGridDay' && info.resource.id) {
                                route = route + `&doctor_id=${info.resource.id}`;
                            }
                            window.location.href = route;
                        }
                    }
                },
                eventClick(info) {
                    if (isCanUpdate && info.event.editable) {
                        window.location.href = window.location.origin + `/appointment/update/${info.event.id}`;
                    }
                },
                eventDidMount(info) {
                    if ((info.event.extendedProps && info.event.extendedProps.patientName) || (info.event.extendedProps && info.event.extendedProps.allDay)) {
                        const tooltipHtml = generateTooltipHtml(info.event.extendedProps);

                        tippy(info.el, {
                            content: tooltipHtml,
                            placement: 'auto-end',
                            theme: 'light',
                            allowHTML: true,
                            interactive: true,
                            appendTo: document.body,
                            popperOptions: {
                                modifiers: [
                                    {
                                        name: 'preventOverflow',
                                        enabled: false
                                    },
                                    {
                                        name: 'hide',
                                        enabled: false
                                    },
                                    {
                                        name: 'flip',
                                        enabled: false
                                    }
                                ]
                            },
                        });
                    }
                },
            },
        }
    });

    window.mainCalendarInstance = ec;

    function getSize(size) {
        if (size === 'sm') {
            return 24;
        } else if (size === 'md') {
            return 30;
        } else if (size === 'lg') {
            return 36;
        } else if (size === 'xl') {
            return 42;
        } else if (size === 'xxl') {
            return 48;
        } else {
            return 30;
        }
    }

    function setSize(size) {
        if (ec && size) {
            ec.setOption('slotHeight', size);
            $('.ec-time-grid .ec-body .ec-time, .ec-time-grid .ec-line').css('height', size + 'px');
        }
    }

    function getFirstDay(firstDay) {
        if (firstDay && firstDay === 'sun') {
            return 0;
        } else {
            return 1;
        }
    }

}

function generateTooltipHtml(props) {
    if (props.allDay) {
        return '<span>🕒 12:00 - 12:00am. 24h 0m</span>';
    }

    const {
        patientName,
        patientCode,
        patientIdentityNumber,
        title,
        details,
        startTime,
        endTime,
        totalTime,
        patientGender,
        patientAge,
        phoneNumber
    } = props;

    // Escape HTML to prevent XSS
    const escapeHtml = (text) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    // Convert line breaks to <br> tags
    const formatDetails = (text) => {
        return escapeHtml(text).replace(/\n/g, '<br>');
    };

    return `
        <div style="max-width: 350px;">
            <div style="border-bottom: 1px solid #ccc; font-weight: bold; margin-bottom: 5px;">${escapeHtml(patientName)}</div>
            <div style="border-bottom: 1px solid #ccc; display: flex; align-items: center; margin-bottom: 5px;">
                <span style="margin-right: 5px;">🕒</span>
                <span>${startTime} - ${endTime}. ${totalTime}</span>
            </div>
            <div>
                <div style="margin-bottom: 2px;">HP: ${escapeHtml(phoneNumber)}</div>
                <div style="margin-bottom: 2px;">Age: ${escapeHtml(patientAge)}, Gender: ${escapeHtml(patientGender)}</div>
                <div style="margin-bottom: 8px;">${escapeHtml(patientCode)} - ${escapeHtml(patientIdentityNumber)}</div>
            </div>
            <div style="border-top: 1px solid #ccc; padding-top: 8px; margin-top: 8px;">
                <h5 style="font-weight: bold">${escapeHtml(title)}</h5>
                <div>${formatDetails(details)}</div>
            </div>
        </div>
    `;
}

export {initCalendar}
